import { customersQuery } from "@common/api/customers"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { CustomerTable } from "./-components/customer-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "./-lib/columns"
import {
  type CustomerFilterValues,
  customerFilterConfigs,
  hasCustomerFilters,
  NO_CUSTOMER_FILTERS,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "./-lib/grouping"
import {
  type CustomerDisplay,
  type CustomerSearch,
  customerSearchSchema,
  directoryOf,
  displayOf,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/customer")({
  staticData: { breadcrumb: "Customers" },
  validateSearch: (search) => customerSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "customer:read", "/"),
  loaderDeps: ({ search }) => directoryOf(search),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(customersQuery(deps)),
  component: CustomersPage,
})

function useUpdateSearch(): (patch: Partial<CustomerSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function CustomersPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={<Toolbar display={display} hidden={fit.hidden} />}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      <CustomerTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() => update({ ...NO_CUSTOMER_FILTERS, search: undefined })}
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: CustomerDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const update = useUpdateSearch()
  const canCreate = useHasPermission("customer:create")

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={values.archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            update({ archived: tab === "archived" || undefined })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={values.search ?? ""}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search customers…"
          />
          <FilterButton
            active={hasCustomerFilters(values)}
            configs={customerFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) =>
              update({ group: next.group, subgroup: next.subgroup })
            }
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["name"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/customer/new">New customer</Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function columnsSearch(columns: Column[]): Partial<CustomerSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function sortSearch(
  display: CustomerDisplay,
  column: Column,
): Partial<CustomerSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "name" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function ActiveFilters(): React.ReactElement {
  const { country, kind } = Route.useSearch()
  const update = useUpdateSearch()
  const values: CustomerFilterValues = { country, kind }

  return (
    <ActiveFilterBar
      active={hasCustomerFilters(values)}
      configs={customerFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_CUSTOMER_FILTERS)}
    />
  )
}
